import type { Celebrity } from "@pushup/types";
import { getTimerLabel, type TimerSnapshot } from "./timer";

/** Normalized MediaPipe-style pose landmark. */
export interface CompositeLandmark {
  x: number;
  y: number;
  visibility?: number;
}

export interface CompositeFrame {
  /** Live camera preview to stamp into the frame (read each frame). */
  video: HTMLVideoElement | null;
  /** Most recent pose landmarks (normalized 0..1), or null when none. */
  landmarks: readonly CompositeLandmark[] | null;
  timer: TimerSnapshot;
  reps: number;
  /** Selected opponent, or null for solo mode. */
  celebrity: Celebrity | null;
  celebrityReps: number;
}

const SKELETON_CONNECTIONS: Array<[number, number]> = [
  // Left arm
  [11, 13],
  [13, 15],
  // Right arm
  [12, 14],
  [14, 16],
  // Shoulders
  [11, 12],
  // Hips
  [23, 24],
  // Torso
  [11, 23],
  [12, 24],
  // Left leg
  [23, 25],
  [25, 27],
  // Right leg
  [24, 26],
  [26, 28],
];

/** Draws a rounded-rect path at (x, y, w, h) with corner radius r. */
function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

const avatarImageCache = new Map<string, HTMLImageElement | null>();

function cachedAvatar(src: string): HTMLImageElement | null {
  if (typeof Image === "undefined") {
    return null;
  }
  if (avatarImageCache.has(src)) {
    return avatarImageCache.get(src) ?? null;
  }
  const img = new Image();
  img.src = src;
  avatarImageCache.set(src, img);
  return img;
}

/**
 * Renders one recorded frame: black backdrop, the (mirrored) camera feed fit
 * into the canvas, the pose skeleton overlaid on it, and the HUD — the same
 * timer/scoreboard the user sees on screen — on top. Pure drawing so it can be
 * driven from a requestAnimationFrame loop (and tested in isolation).
 */
export function drawCompositeFrame(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  frame: CompositeFrame,
): void {
  ctx.save();
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, width, height);

  // Selfie mirror: flip the whole feed (video + skeleton) horizontally.
  ctx.translate(width, 0);
  ctx.scale(-1, 1);

  if (frame.video && frame.video.videoWidth > 0) {
    const vw = frame.video.videoWidth;
    const vh = frame.video.videoHeight;
    const scale = Math.min(width / vw, height / vh);
    const dw = vw * scale;
    const dh = vh * scale;
    const dx = (width - dw) / 2;
    const dy = (height - dh) / 2;

    ctx.drawImage(frame.video, dx, dy, dw, dh);

    if (frame.landmarks && frame.landmarks.length > 0) {
      drawSkeleton(ctx, frame.landmarks, dx, dy, dw, dh);
    }
  }

  ctx.restore();

  // HUD is drawn in un-mirrored canvas space so text reads correctly.
  drawHud(ctx, width, height, frame);
}

function drawSkeleton(
  ctx: CanvasRenderingContext2D,
  landmarks: readonly CompositeLandmark[],
  dx: number,
  dy: number,
  dw: number,
  dh: number,
): void {
  const project = (l: CompositeLandmark) => ({ x: dx + l.x * dw, y: dy + l.y * dh });

  ctx.save();
  ctx.lineWidth = 4;
  ctx.strokeStyle = "rgba(16, 185, 129, 0.8)";
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  for (const [i, j] of SKELETON_CONNECTIONS) {
    const p1 = landmarks[i];
    const p2 = landmarks[j];
    if (p1 && p2 && (p1.visibility ?? 1) >= 0.5 && (p2.visibility ?? 1) >= 0.5) {
      const a = project(p1);
      const b = project(p2);
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
    }
  }

  for (let i = 0; i < landmarks.length; i++) {
    const p = landmarks[i];
    if (!p || (p.visibility ?? 1) < 0.5) continue;
    const pt = project(p);
    if (i === 13 || i === 14) {
      ctx.beginPath();
      ctx.arc(pt.x, pt.y, 10, 0, 2 * Math.PI);
      ctx.fillStyle = "#f59e0b";
      ctx.fill();
      ctx.lineWidth = 2;
      ctx.strokeStyle = "#ffffff";
      ctx.stroke();
    } else if ([11, 12, 15, 16, 23, 24, 25, 26, 27, 28].includes(i)) {
      ctx.beginPath();
      ctx.arc(pt.x, pt.y, 6, 0, 2 * Math.PI);
      ctx.fillStyle = "#10b981";
      ctx.fill();
    }
  }

  ctx.restore();
}

function drawHud(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  frame: CompositeFrame,
): void {
  const { label, low } = getTimerLabel(frame.timer);
  drawTimerCapsule(ctx, width, label, low);
  if (frame.celebrity) {
    drawCompetitiveScoreboard(ctx, width, frame.reps, frame.celebrity, frame.celebrityReps);
  } else {
    drawSoloScoreboard(ctx, width, frame.reps);
  }
}

function drawTimerCapsule(
  ctx: CanvasRenderingContext2D,
  width: number,
  label: string,
  low: boolean,
): void {
  const w = 176;
  const h = 48;
  const x = (width - w) / 2;
  const y = 18;

  ctx.save();
  roundedRect(ctx, x, y, w, h, h / 2);
  ctx.fillStyle = "rgba(4, 7, 13, 0.92)";
  ctx.fill();
  ctx.lineWidth = 2;
  ctx.strokeStyle = low ? "rgba(255, 42, 75, 0.9)" : "rgba(255, 255, 255, 0.25)";
  ctx.stroke();

  ctx.font = "italic 700 26px 'JetBrains Mono', ui-monospace, monospace";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = low ? "#f87171" : "#ffffff";
  ctx.shadowColor = "rgba(0, 0, 0, 0.9)";
  ctx.shadowBlur = 8;
  ctx.fillText(label, width / 2, y + h / 2 + 1);
  ctx.restore();
}

function drawSoloScoreboard(ctx: CanvasRenderingContext2D, width: number, reps: number): void {
  const cx = width / 2;
  const top = 86;
  const avatarR = 26;

  ctx.save();
  // Avatar ring
  ctx.beginPath();
  ctx.arc(cx, top + avatarR, avatarR, 0, 2 * Math.PI);
  ctx.fillStyle = "rgba(7, 17, 30, 0.95)";
  ctx.fill();
  ctx.lineWidth = 3;
  ctx.strokeStyle = "#00a2ff";
  ctx.stroke();

  // Person glyph (simplified)
  ctx.fillStyle = "rgba(255, 255, 255, 0.9)";
  ctx.beginPath();
  ctx.arc(cx, top + avatarR - 8, 8, 0, 2 * Math.PI);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(cx, top + avatarR + 10, 13, 0, Math.PI);
  ctx.fill();

  // Rep count + label
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = "italic 900 56px ui-sans-serif, system-ui, sans-serif";
  ctx.fillStyle = "#ffffff";
  ctx.shadowColor = "rgba(0, 0, 0, 0.6)";
  ctx.shadowBlur = 10;
  ctx.fillText(String(reps), cx, top + 2 * avatarR + 46);
  ctx.shadowBlur = 0;
  ctx.font = "italic 800 16px ui-sans-serif, system-ui, sans-serif";
  ctx.fillStyle = "#00a2ff";
  ctx.fillText("REPS", cx, top + 2 * avatarR + 76);
  ctx.restore();
}

function drawCompetitiveScoreboard(
  ctx: CanvasRenderingContext2D,
  width: number,
  reps: number,
  celebrity: Celebrity,
  celebrityReps: number,
): void {
  const cx = width / 2;
  const top = 84;
  const avatarR = 30;
  const spacing = 150;

  const total = reps + celebrityReps;
  const userPercent =
    total === 0 ? 50 : Math.min(95, Math.max(5, Math.round((reps / total) * 100)));

  ctx.save();
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  // Left: YOU (blue)
  drawAvatarCircle(ctx, cx - spacing, top + avatarR, avatarR, "#00a2ff", "#0e1d33", null, "Y");
  ctx.font = "italic 800 16px ui-sans-serif, system-ui, sans-serif";
  ctx.fillStyle = "#00a2ff";
  ctx.fillText("YOU", cx - spacing, top + 2 * avatarR + 28);
  ctx.font = "italic 900 46px ui-sans-serif, system-ui, sans-serif";
  ctx.fillStyle = "#ffffff";
  ctx.shadowColor = "rgba(0, 0, 0, 0.6)";
  ctx.shadowBlur = 10;
  ctx.fillText(String(reps), cx - spacing, top + 2 * avatarR + 66);

  // Right: opponent (red)
  drawAvatarCircle(
    ctx,
    cx + spacing,
    top + avatarR,
    avatarR,
    "#ff2a4b",
    "#1e070c",
    celebrity.imageDataUrl || null,
    celebrity.name.charAt(0),
  );
  ctx.font = "italic 800 16px ui-sans-serif, system-ui, sans-serif";
  ctx.fillStyle = "#ff2a4b";
  const name = celebrity.name.toUpperCase();
  ctx.fillText(
    name.length > 12 ? `${name.slice(0, 11)}...` : name,
    cx + spacing,
    top + 2 * avatarR + 28,
  );
  ctx.font = "italic 900 46px ui-sans-serif, system-ui, sans-serif";
  ctx.fillStyle = "#ffffff";
  ctx.shadowBlur = 10;
  ctx.fillText(String(celebrityReps), cx + spacing, top + 2 * avatarR + 66);
  ctx.shadowBlur = 0;

  // VS divider
  ctx.font = "italic 900 24px ui-sans-serif, system-ui, sans-serif";
  ctx.fillStyle = "rgba(255, 255, 255, 0.85)";
  ctx.shadowColor = "rgba(255, 255, 255, 0.3)";
  ctx.shadowBlur = 8;
  ctx.fillText("VS", cx, top + avatarR + 4);
  ctx.shadowBlur = 0;

  // Ratio bar
  const barY = top + 2 * avatarR + 108;
  const barW = 320;
  const barH = 14;
  const barX = cx - barW / 2;

  roundedRect(ctx, barX, barY, barW, barH, barH / 2);
  ctx.fillStyle = "#04070d";
  ctx.fill();

  const userW = Math.round(barW * (userPercent / 100));
  if (userW > 0) {
    roundedRect(ctx, barX, barY, userW, barH, barH / 2);
    ctx.fillStyle = "rgba(0, 162, 255, 0.85)";
    ctx.fill();
  }
  if (userW < barW) {
    roundedRect(ctx, barX + userW, barY, barW - userW, barH, barH / 2);
    ctx.fillStyle = "rgba(255, 42, 75, 0.85)";
    ctx.fill();
  }
  ctx.lineWidth = 2;
  ctx.strokeStyle = "rgba(255, 255, 255, 0.25)";
  roundedRect(ctx, barX, barY, barW, barH, barH / 2);
  ctx.stroke();

  ctx.restore();
}

function drawAvatarCircle(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  ringColor: string,
  fillColor: string,
  imageSrc: string | null,
  initial: string,
): void {
  ctx.beginPath();
  ctx.arc(x, y, r, 0, 2 * Math.PI);
  ctx.fillStyle = fillColor;
  ctx.fill();
  ctx.lineWidth = 3;
  ctx.strokeStyle = ringColor;
  ctx.stroke();

  if (imageSrc) {
    const img = cachedAvatar(imageSrc);
    if (img && img.complete && img.naturalWidth > 0) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(x, y, r, 0, 2 * Math.PI);
      ctx.clip();
      ctx.drawImage(img, x - r, y - r, r * 2, r * 2);
      ctx.restore();
      return;
    }
  }

  // Fallback: initial letter on the dark avatar circle.
  ctx.fillStyle = "rgba(255, 255, 255, 0.9)";
  ctx.font = "700 26px ui-sans-serif, system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(initial, x, y + 1);
}
